import type { SessionView } from "../bridge/relayClient";

/**
 * When the MCP control app claims its project, and what its panel says about
 * the agent's target (#67, `docs/session-model.md`).
 *
 * Pure, so the rule is tested without a DOM; `controlAppMain.ts` feeds it the
 * document's visibility and the bridge's status.
 */

export type FrontInputs = {
  readonly visible: boolean;
  readonly connected: boolean;
  /** This panel's project was the active one when the relay last said. */
  readonly claimed: boolean;
};

/**
 * True on the transition INTO "connected, and either visible or holding the
 * project", and only then.
 *
 * Becoming visible is the tab switch the user makes. Connecting while visible
 * covers the panel opened in a tab already in front — no `visibilitychange`
 * fires there. Connecting while HIDDEN but holding the project is a reconnect
 * after a drop, while the user talks to the agent in another tab: the relay
 * forgot the target with the sockets, and without re-claiming it the agent is
 * stranded until the user comes back. Sending on every status while this holds
 * would be a message per status update, each waking the relay for nothing.
 */
export function shouldSendFront(previous: FrontInputs, next: FrontInputs): boolean {
  const wants = (inputs: FrontInputs): boolean =>
    inputs.connected && (inputs.visible || inputs.claimed);
  return wants(next) && !wants(previous);
}

/** The panel's one line about the agent's target. Empty until the relay has said. */
export function sessionLine(session: SessionView | null): string {
  if (session === null) return "";
  if (session.activeHere) return "The agent is working in this project.";
  if (!session.controlHere) {
    return (
      "The relay does not recognise this panel as the MCP control app. Open it as the Giraffe " +
      "MCP app, on the v6 frontend, in a workspace where that app is enabled."
    );
  }
  if (session.activeProjectId === null) {
    return "No project is active yet. Keep this tab in front to make it the agent's target.";
  }
  return "Another project is active. Bring this tab to the front to switch.";
}
